import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import Swal from "sweetalert2";
import authservice from "../services/auth.services";

const StoreStockContext = createContext();

export const useStoreStock = () => {
  const context = useContext(StoreStockContext);
  if (!context) {
    throw new Error("useStoreStock must be used within a StoreStockProvider");
  }
  return context;
};

const StoreStockProvider = ({ children }) => {
  const userKey = localStorage.getItem("userKey");
  const [sortByPrice, setSortByPrice] = useState("");
  const [isAvailable, setIsAvailable] = useState("");
  const [sortByDistance, setSortByDistance] = useState("");
  const [viewMoreState, setViewMoreState] = useState({});
  const [storeStocksData, setStoreStocksData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showStoreStock, setShowStoreStock] = useState(false);

  const getStoresDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authservice.postJsonData(
        "getBucketProductStores",
        {
          latitude: parseFloat(localStorage.getItem("selectedCityLat")),
          longitude: parseFloat(localStorage.getItem("selectedCityLng")),
          userKey: localStorage.getItem("userKey"),
          sortByPrice,
          sortByDistance,
          isAvailable,
        },
      );
      if (response.apiResponseCode == "1001" && Array.isArray(response.data)) {
        setShowStoreStock(true);
        setStoreStocksData(response.data);
        setLoading(false);
      } else {
        setLoading(false);
        setStoreStocksData([]);
        setShowStoreStock(true);
      }
    } catch (err) {
      console.warn("Error fetching store details:", err);
      setError(err);
      setLoading(false);
      setStoreStocksData([]);
      setShowStoreStock(false);
    }
  }, [userKey, sortByPrice, sortByDistance, isAvailable]);

  const placeOrder = useCallback(
    async ({ storeID, businessName }) => {
      try {
        const response = await authservice.postJsonData("placeOrder", {
          storeID,
          userKey,
        });
        if (response.apiResponseCode === "1001") {
          if (response.webUrl?.String) {
            const cont = await Swal.fire({
              title: "Cart Placed!",
              html: `
                <img
                 src="https://cdn-icons-png.flaticon.com/512/190/190411.png"
                 alt="Order Success"
                 style="width: 60px; height: 60px; margin-bottom: 15px;"
                />            
                <p style="font-size: 16px; font-weight: 600">Your cart has been placed successfully in <span style="color: green;"> ${businessName}</span> store.</p>
                <div style="text-align: center; font-size: 14px;">
                <p style="margin-bottom: 15px;">To order please follow below steps:</p>
                   <div style="display: flex; flex-wrap: wrap; justify-content: center; align-items: center; gap: 15px;">
            
            <div style="text-align: center;">
              <img
                src="https://cdn-icons-png.flaticon.com/512/891/891462.png"
                alt="Cart"
                style="width: 40px; height: 40px; margin-bottom: 5px;"
              /><br />
              <span style="font-weight: 500;">Open Cart</span>
            </div>
    
            <span style="font-size: 1.5rem;">»</span>
    
            <div style="text-align: center;">
              <img
                src="https://cdn-icons-png.flaticon.com/512/891/891419.png"
                alt="Checkout"
                style="width: 40px; height: 40px; margin-bottom: 5px;"
              /><br />
              <span style="font-weight: 500;">Checkout</span>
            </div>
    
            <span style="font-size: 1.5rem;">»</span>
    
            <div style="text-align: center;">
              <img
                src="https://cdn-icons-png.flaticon.com/512/1828/1828506.png"
                alt="Login"
                style="width: 40px; height: 40px; margin-bottom: 5px;"
              /><br />
              <span style="font-weight: 500;">Login</span>
            </div>
    
            <span style="font-size: 1.5rem;">»</span>
    
            <div style="text-align: center;">
              <img
                src="https://cdn-icons-png.flaticon.com/512/1040/1040987.png"
                alt="Place Order"
                style="width: 40px; height: 40px; margin-bottom: 5px;"
              /><br />
              <span style="font-weight: 500;">Place Order</span>
            </div>
          </div>
        </div>
              `,
              confirmButtonText: "Continue",
              allowOutsideClick: false,
            });

            if (!cont.isConfirmed) return;
            const redirectUrl = response.webUrl.String.startsWith("http")
              ? response?.webUrl?.String
              : `https://${response?.webUrl?.String}`;

            const url = new URL(redirectUrl);
            const urlJson = {
              storeID,
              userKey,
            };
            const base64Payload = btoa(JSON.stringify(urlJson));
            url.searchParams.append("data", base64Payload);
            window.open(url.toString(), "_blank");
            return response;
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Order Failed",
            text: response?.apiResponseMsg,
          });
        }
      } catch (errors) {
        console.warn("Error placing order:", errors);
        Swal.fire({
          icon: "error",
          title: "Server Error",
          text: "Could not place the order. Please try again later.",
        });
      }
    },
    [userKey, storeStocksData],
  );

  useEffect(() => {
    if (showStoreStock == true) {
      getStoresDetails();
    }
  }, [sortByPrice, sortByDistance, isAvailable]);

  const contextValue = useMemo(
    () => ({
      storeStocksData,
      showStoreStock,
      loading,
      error,
      getStoresDetails,
      setShowStoreStock,
      setStoreStocksData,
      setError,
      placeOrder,
      sortByPrice,
      setSortByPrice,
      sortByDistance,
      setSortByDistance,
      isAvailable,
      setIsAvailable,
      viewMoreState,
      setViewMoreState,
    }),
    [
      storeStocksData,
      loading,
      error,
      getStoresDetails,
      showStoreStock,
      placeOrder,
      sortByPrice,
      sortByDistance,
      isAvailable,
      viewMoreState,
      setViewMoreState,
    ],
  );

  return (
    <StoreStockContext.Provider value={contextValue}>
      {children}
    </StoreStockContext.Provider>
  );
};

export default StoreStockProvider;
